import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { Inputs } from '../utils/roiCalculations';
import { RECRUITER_MONTHLY_FEE } from '../utils/roiCalculations';
import { INITIAL_PRESET_STATE, RetentionPresetState } from '../utils/retentionPresets';
export interface CalculatorScenario {
  halfSales: boolean;
  doubleAttrition: boolean;
}

/**
 * "monthly"  — default: implementation fee, $2,500 partnership + $1,250
 *              pipeline per $2,500 bucket, and the $500 placement bonus.
 * "flagship" — the same, plus a $1,000 retention bonus at day 45.
 * Presenter-only switch (click "Flagship Partnership" on the Partnership page
 * or the calculator's package badge). The implementation fee has its own
 * switch in the proposal's Tailor panel.
 */
export type PricingModel = 'monthly' | 'flagship';
export type ImplementationOption = 'full' | 'split' | 'promo';
/** One-time promo implementation price, leader add-on included:
 *  $3,000 (regularly $4,500), or $4,500 with a recruiter (regularly $6,000). */
export const PROMO_IMPLEMENTATION_FEE = 3000;
export const PROMO_IMPLEMENTATION_FEE_RECRUITER = 4500;
export function promoImplementationFee(hasRecruiter: boolean) {
  return hasRecruiter ? PROMO_IMPLEMENTATION_FEE_RECRUITER : PROMO_IMPLEMENTATION_FEE;
}
function defaultPromoDeadline() {
  const date = new Date();
  date.setDate(date.getDate() + 3);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** "2026-11-12" → "November 12". */
export function formatPromoDeadline(iso: string) {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric'
  });
}
export type PricingLayout = 'tiers' | 'row' | 'twoStep' | 'feeList' | 'tiersStrip';
interface PricingState {
  /** Partner name for the Partnership Brief. Set from the ROI calculator's
   *  company field or typed straight into the brief header. */
  companyName: string;
  setCompanyName: (next: string) => void;
  /** The ROI calculator's inputs once they've been changed (null until then),
   *  read by the Financial Model PDF. */
  calculatorInputs: Inputs | null;
  setCalculatorInputs: (next: Inputs | null) => void;
  /** Retention preset selection (Low / On Target / +5%), shared by the
   *  calculator and the proposal's Tailor panel. */
  retentionPresets: RetentionPresetState;
  setRetentionPresets: (next: RetentionPresetState) => void;
  /** Which layout page 2 of the proposal uses for pricing (preview switch). */
  pricingLayout: PricingLayout;
  setPricingLayout: (next: PricingLayout) => void;
  /** The calculator's active stress-test scenarios. */
  calculatorScenario: CalculatorScenario;
  setCalculatorScenario: (next: CalculatorScenario) => void;
  /** Whether the performance guarantee has been revealed (card flipped). */
  guaranteeRevealed: boolean;
  setGuaranteeRevealed: (next: boolean) => void;
  /** Hires guaranteed in the first 90 days: 90 days of full ad spend at the
   *  top of the cost-per-hire range + $150. */
  guaranteedHires: number;
  pricingModel: PricingModel;
  setPricingModel: (next: PricingModel) => void;
  /** True when the model charges a one-time implementation fee. */
  hasImplementationFee: boolean;
  setHasImplementationFee: (next: boolean) => void;
  /** True when the Flagship $1,000 day-45 retention bonus applies. */
  hasRetentionBonuses: boolean;
  /** Implementation fee before any recruiter uplift. */
  baseBuildFee: number;
  adSpend: number;
  cpaLow: number;
  cpaHigh: number;
  hasRecruiter: boolean;
  hasLeader: boolean;
  splitBuildFee: boolean;
  /** How the implementation fee is paid: in full, split over 3 months, or
   *  the one-time promo price for signing by the promo deadline. */
  implementationOption: ImplementationOption;
  setImplementationOption: (next: ImplementationOption) => void;
  /** True when the promo price applies (Flagship + promo selected). */
  isPromo: boolean;
  /** Promo deadline as YYYY-MM-DD. */
  promoDeadline: string;
  setPromoDeadline: (next: string) => void;
  /** Implementation fee (incl. add-ons) before any promo. */
  regularImplementationFee: number;
  setAdSpend: (next: number) => void;
  setCpaLow: (next: number) => void;
  setCpaHigh: (next: number) => void;
  setHasRecruiter: (next: boolean) => void;
  setHasLeader: (next: boolean) => void;
  setSplitBuildFee: (next: boolean) => void;
  /** Whether the 30-day money-back guarantee is offered (on by default). */
  moneyBackGuarantee: boolean;
  setMoneyBackGuarantee: (next: boolean) => void;
  /** Go-to-market switch in the proposal's side panel. */
  goToMarket: boolean;
  setGoToMarket: (next: boolean) => void;
  /** Go-to-market framing applies only alongside the leader search. */
  isGoToMarket: boolean;
  // Derived
  buildFee: number;
  adSpendChunks: number;
  scopeMultiplier: number;
  platformFee: number;
  campaignFee: number;
  recruiterFee: number;
  monthlyTotal: number;
  hiresLow: number;
  hiresHigh: number;
  // Leadership add-on
  leaderBuildFee: number;
  leaderMonthlyFee: number;
  leaderActiveFee: number;
  leaderAdSpendLow: number;
  leaderAdSpendHigh: number;
}
const PricingContext = createContext<PricingState | null>(null);
export function PricingProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [adSpend, setAdSpend] = useState(5000);
  const [cpaLow, setCpaLow] = useState(200);
  const [cpaHigh, setCpaHigh] = useState(500);
  const [hasRecruiter, setHasRecruiter] = useState(false);
  const [hasLeader, setHasLeaderState] = useState(false);
  // Split and promo can be combined: the promo price, paid in three.
  const [splitOn, setSplitOn] = useState(false);
  const [promoOn, setPromoOn] = useState(false);
  const implementationOption: ImplementationOption = promoOn ? 'promo' : splitOn ? 'split' : 'full';
  // "full" clears both; "split" and "promo" each toggle on their own.
  const setImplementationOption = useCallback((next: ImplementationOption) => {
    if (next === 'full') {
      setSplitOn(false);
      setPromoOn(false);
    } else if (next === 'split') {
      setSplitOn(on => !on);
    } else {
      setPromoOn(on => !on);
    }
  }, []);
  const [promoDeadline, setPromoDeadline] = useState(defaultPromoDeadline);
  // The deck's split button still works: it switches between full and split.
  const setSplitBuildFee = useCallback((next: boolean) => {
    setSplitOn(next);
    setPromoOn(false);
  }, []);
  const [goToMarket, setGoToMarket] = useState(false);
  const [moneyBackGuarantee, setMoneyBackGuarantee] = useState(true);
  // Default: no day-45 retention bonus; Flagship turns it on.
  const [pricingModel, setPricingModel] = useState<PricingModel>('monthly');
  // Default: the implementation fee, paid in full at signing.
  const [hasImplementationFee, setHasImplementationFee] = useState(true);
  const [guaranteeRevealed, setGuaranteeRevealed] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [calculatorInputs, setCalculatorInputs] = useState<Inputs | null>(null);
  const [retentionPresets, setRetentionPresets] = useState<RetentionPresetState>(INITIAL_PRESET_STATE);
  const [pricingLayout, setPricingLayout] = useState<PricingLayout>('row');
  const [calculatorScenario, setCalculatorScenario] = useState<CalculatorScenario>({
    halfSales: false,
    doubleAttrition: false
  });
  // The leader search runs on its own fixed $2,500 ad spend and $2,500/mo
  // service (leaderAdSpendLow / leaderMonthlyFee) for the search window only.
  // The standard campaign's ad spend and fees are left untouched.
  const setHasLeader = useCallback((next: boolean) => {
    setHasLeaderState(next);
  }, []);
  const value = useMemo<PricingState>(() => {
    const isFlagship = pricingModel === 'flagship';
    const impl = hasImplementationFee;
    // Implementation: flat $4,500 at any scope, +$1,500 with a recruiter.
    const baseBuildFee = impl ? 4500 : 0;
    const regularBuildFee = baseBuildFee + (impl && hasRecruiter ? 1500 : 0);
    const regularLeaderBuildFee = impl ? 1000 : 0;
    // Promo: one flat price, paid once, add-ons included.
    const isPromo = impl && promoOn;
    const buildFee = isPromo ? promoImplementationFee(hasRecruiter) : regularBuildFee;
    // The split only exists when there is a fee to split.
    const splitBuildFee = impl && splitOn;
    // Ad spend drives scope: one chunk per $2,500 under management.
    const adSpendChunks = Math.max(1, Math.ceil(adSpend / 2500));
    // Scope is counted in whole steps: one per $2,500 of ad spend.
    const scopeMultiplier = adSpendChunks;
    // Both models share the same monthly fees and placement bonus; Flagship
    // only adds the one-time implementation fee.
    const platformFee = 2500;
    const pipelinePerBucket = 1250;
    const campaignFee = pipelinePerBucket * adSpendChunks;
    const recruiterFee = RECRUITER_MONTHLY_FEE * adSpendChunks;
    // A leadership campaign runs at $2,500 of managed ad spend (1 bucket).
    const leaderAdSpend = 2500;
    const leaderMonthlyFee = platformFee + pipelinePerBucket * Math.ceil(leaderAdSpend / 2500);
    const monthlyTotal = platformFee + campaignFee + (hasRecruiter ? recruiterFee : 0);
    const hiresLow = Math.max(1, Math.round(adSpend / cpaHigh));
    const hiresHigh = Math.max(hiresLow + 1, Math.round(adSpend / cpaLow));
    return {
      companyName,
      setCompanyName,
      calculatorInputs,
      setCalculatorInputs,
      retentionPresets,
      setRetentionPresets,
      pricingLayout,
      setPricingLayout,
      calculatorScenario,
      setCalculatorScenario,
      guaranteeRevealed,
      setGuaranteeRevealed,
      // Flat-spend estimate only; the documents and guarantee card use
      // useGuaranteedHires, which reads the modeled Months 1–3 ad spend.
      guaranteedHires: Math.max(1, Math.floor(adSpend * 3 / cpaHigh)),
      pricingModel,
      setPricingModel,
      hasImplementationFee: impl,
      setHasImplementationFee,
      hasRetentionBonuses: isFlagship,
      baseBuildFee,
      adSpend,
      cpaLow,
      cpaHigh,
      hasRecruiter,
      hasLeader,
      splitBuildFee,
      implementationOption,
      setImplementationOption,
      isPromo,
      promoDeadline,
      setPromoDeadline,
      regularImplementationFee: regularBuildFee + (hasLeader ? regularLeaderBuildFee : 0),
      setAdSpend,
      setCpaLow,
      setCpaHigh,
      setHasRecruiter,
      setHasLeader,
      setSplitBuildFee,
      moneyBackGuarantee,
      setMoneyBackGuarantee,
      goToMarket,
      setGoToMarket,
      isGoToMarket: goToMarket && hasLeader,
      buildFee,
      adSpendChunks,
      scopeMultiplier,
      platformFee,
      campaignFee,
      recruiterFee,
      monthlyTotal,
      hiresLow,
      hiresHigh,
      // Implementation adds $1,000 for the leader search.
      // The promo price already includes the leader add-on.
      leaderBuildFee: isPromo ? 0 : regularLeaderBuildFee,
      // Leadership monthly service follows the standard campaign structure,
      // priced at the $2,500 of ad spend the leadership campaign runs on.
      leaderMonthlyFee,
      // Leaders earn the same bonuses as a standard campaign hire, so there is
      // no separate monthly leader fee.
      leaderActiveFee: 0,
      leaderAdSpendLow: leaderAdSpend,
      leaderAdSpendHigh: leaderAdSpend
    };
  }, [companyName, calculatorInputs, retentionPresets, pricingLayout, calculatorScenario, guaranteeRevealed, pricingModel, hasImplementationFee, adSpend, cpaLow, cpaHigh, hasRecruiter, hasLeader, implementationOption, splitOn, promoOn, setImplementationOption, promoDeadline, setSplitBuildFee, goToMarket, moneyBackGuarantee, setHasLeader]);
  return <PricingContext.Provider value={value} data-id="element-1768" data-mp="9deuvo5pnkm6">{children}</PricingContext.Provider>;
}
export function usePricing(): PricingState {
  const context = useContext(PricingContext);
  if (!context) {
    throw new Error('usePricing must be used within a PricingProvider');
  }
  return context;
}